import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from 'components/ui/form';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useSettingsDialogStore } from 'state/store/settings-dialog/useSettingsDialogStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useGetBillingProfile, useUpdateBillingProfile } from '../hooks/use-subscription';
import { buildUpdateBillingProfileRequest } from '../services/subscription-service';
import {
  billingFormFromProfile,
  createBillingSchema,
  emptyBillingForm,
} from '../utils/billing-form';

interface BillingDetailsFormProps {
  onSaved?: () => void;
  /** Settings edits the details at any time; a payment asks for them because it needs them. */
  purpose?: 'payment' | 'settings';
  /** Hides Save and Open billing settings, for a caller that saves through the ref instead. */
  hideActions?: boolean;
}

/** Lets a caller save the details from its own button. Resolves true once they are saved. */
export type BillingDetailsFormHandle = { save: () => Promise<boolean> };

type Field = keyof z.infer<ReturnType<typeof createBillingSchema>>;

const FIELDS: { name: Field; label: string; fallback: string; wide?: boolean }[] = [
  { name: 'legalName', label: 'LEGAL_NAME', fallback: 'Legal name', wide: true },
  { name: 'billingContactName', label: 'BILLING_CONTACT', fallback: 'Billing contact' },
  { name: 'billingContactEmail', label: 'BILLING_EMAIL', fallback: 'Billing email' },
  { name: 'line1', label: 'ADDRESS', fallback: 'Address', wide: true },
  { name: 'postalCode', label: 'POSTAL_CODE', fallback: 'Postal code' },
  { name: 'city', label: 'CITY', fallback: 'City' },
  { name: 'countryCode', label: 'COUNTRY_CODE', fallback: 'Country code' },
  {
    name: 'taxRegistrationId',
    label: 'TAX_OR_VAT_REGISTRATION',
    fallback: 'Tax or VAT registration',
  },
];

/**
 * The organization's billing details, in place, for when a payment needs them. The full checkout
 * page stays one click away in case saving here does not work.
 */
export const BillingDetailsForm = forwardRef<BillingDetailsFormHandle, BillingDetailsFormProps>(
  ({ onSaved, purpose = 'payment', hideActions = false }, ref) => {
    const { t } = useTranslation();
    const openSettings = useSettingsDialogStore((s) => s.openSettings);
    const user = useCommonStore((s) => s.user);
    const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
    const organizationId = selectedOrgId || user?.organizationId || '';
    const { data: profile, isLoading } = useGetBillingProfile();
    const {
      mutateAsync: save,
      isPending,
      isError,
    } = useUpdateBillingProfile({ onSuccess: onSaved });

    const form = useForm<z.infer<ReturnType<typeof createBillingSchema>>>({
      resolver: zodResolver(createBillingSchema(t)),
      defaultValues: emptyBillingForm,
    });

    useEffect(() => {
      if (isLoading) return;
      form.reset(
        billingFormFromProfile(profile, {
          name: [user?.firstName, user?.lastName].filter(Boolean).join(' '),
          email: user?.email ?? '',
        })
      );
    }, [isLoading, profile, user, form]);

    const onSubmit = form.handleSubmit(async (values) => {
      await save(buildUpdateBillingProfileRequest(organizationId, values)).catch(() => undefined);
    });

    useImperativeHandle(ref, () => ({
      save: async () => {
        let isSaved = false;
        await form
          .handleSubmit(async (values) => {
            await save(buildUpdateBillingProfileRequest(organizationId, values));
            isSaved = true;
          })()
          .catch(() => undefined);
        return isSaved;
      },
    }));

    return (
      <Form {...form}>
        <div
          className={
            hideActions ? 'flex flex-col gap-3' : 'flex flex-col gap-3 rounded-lg border p-4'
          }
        >
          {/* The caller's pay button saves these, so a short header is enough. A settings dialog has its own. */}
          {hideActions && purpose === 'payment' && (
            <div className="flex items-baseline justify-between gap-4">
              <p className="font-semibold">{t('BILLING_DETAILS', 'Billing details')}</p>
              <p className="text-xs text-muted-foreground">
                {t('SAVED_WHEN_YOU_PAY', 'Saved when you pay')}
              </p>
            </div>
          )}
          {!hideActions && (
            <div>
              <p className="font-medium">
                {purpose === 'payment'
                  ? t('BILLING_DETAILS_NEEDED', 'Billing details are needed to pay')
                  : t('BILLING_DETAILS', 'Billing details')}
              </p>
              <p className="text-sm text-muted-foreground">
                {profile?.missingFields?.length
                  ? t('BILLING_MISSING_FIELDS', {
                      fields: profile.missingFields.join(', '),
                      defaultValue:
                        'Missing: {{fields}}. They go on the invoice and are saved for next time.',
                    })
                  : t(
                      'BILLING_SAVED_FOR_NEXT_TIME',
                      'They go on the invoice and are saved for next time.'
                    )}
              </p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map((field) => (
              <FormField
                key={field.name}
                control={form.control}
                name={field.name}
                render={({ field: input }) => (
                  <FormItem className={field.wide ? 'sm:col-span-2' : undefined}>
                    <FormLabel>{t(field.label, field.fallback)}</FormLabel>
                    <FormControl>
                      <Input
                        {...input}
                        value={input.value ?? ''}
                        className="rounded-xl border-grey-100 dark:border-border"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
          </div>
          {hideActions && isError && (
            <span className="text-sm text-destructive">
              {t('BILLING_SAVE_FAILED', 'The details could not be saved. Try again in a moment.')}
            </span>
          )}
          {!hideActions && (
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" onClick={onSubmit} disabled={isPending || isLoading}>
                {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('SAVE_BILLING_DETAILS', 'Save billing details')}
              </Button>
              {purpose === 'payment' && (
                <Button
                  type="button"
                  variant="link"
                  className="text-foreground"
                  onClick={() => openSettings('subscription')}
                >
                  {t('OPEN_BILLING_SETTINGS', 'Open billing settings')}
                </Button>
              )}
              {isError && (
                <span className="text-sm text-destructive">
                  {t(
                    'BILLING_SAVE_FAILED',
                    'The details could not be saved. Try again in a moment.'
                  )}
                </span>
              )}
            </div>
          )}
        </div>
      </Form>
    );
  }
);

BillingDetailsForm.displayName = 'BillingDetailsForm';
